'use client';

import { PrismicRichText } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import { useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";

/**
 * @typedef {import("@prismicio/client").Content.TestimonialsSlice} TestimonialsSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<TestimonialsSlice>} TestimonialsProps
 * @type {import("react").FC<TestimonialsProps>}
 */
const Testimonials = ({ slice }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  // Vérifier si le titre est un h1
  const isH1 = slice.primary.title?.[0]?.type === 'heading1';

  // Animation de texte pour le h1
  useEffect(() => {
    if (!isInView || isTyping || !isH1) return;

    // Extraire le texte du strong uniquement
    const strongNode = slice.primary.title?.[0]?.spans?.find(span => span.type === 'strong');
    if (!strongNode) return;

    const fullText = slice.primary.title[0].text;
    const strongText = fullText.slice(strongNode.start, strongNode.end);

    // Délai de 300ms avant de commencer l'animation
    setTimeout(() => {
      setIsTyping(true);
      let currentIndex = 0;

      const interval = setInterval(() => {
        if (currentIndex <= strongText.length) {
          setDisplayedText(strongText.slice(0, currentIndex));
          currentIndex++;
        } else {
          clearInterval(interval);
          setIsTyping(false);
        }
      }, 100);

      return () => clearInterval(interval);
    }, 300);
  }, [isInView, slice.primary.title, isH1]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="pt-10 pb-20 md:pt-20 md:pb-40"
    >
      <div className="max-w-250 mx-auto w-full px-8 md:px-16">
        {/* Titre */}
        {isFilled.richText(slice.primary.title) && (
          <div className="mb-10" ref={ref}>
            <PrismicRichText
              field={slice.primary.title}
              components={{
                heading1: ({ children }) => <h1 className="h1 uppercase">{children}</h1>,
                heading2: ({ children }) => <h2 className="h2">{children}</h2>,
                heading3: ({ children }) => <h3 className="h3">{children}</h3>,
                strong: ({ children }) => (
                  <strong className={isH1 ? "relative" : ""}>
                    {isH1 && <span className="invisible">{children}</span>}
                    <span className={isH1 ? "absolute inset-[1px]" : ""}>
                      {isH1 ? (displayedText || "") : children}
                    </span>
                  </strong>
                ),
              }}
            />
          </div>
        )}

        {/* Liste des témoignages */}
        {isFilled.group(slice.primary.testimonials) && (
          <div className="flex flex-col gap-8">
            {slice.primary.testimonials.map((item, index) => (
              <div key={index} className="flex flex-col gap-4 bg-green-light/30 p-8 md:p-10">
                {/* Témoignage */}
                {isFilled.richText(item.testimonial) && (
                  <PrismicRichText
                    field={item.testimonial}
                    components={{
                      paragraph: ({ children }) => <p className="text-base italic">{children}</p>,
                      strong: ({ children }) => <strong>{children}</strong>,
                      em: ({ children }) => <em>{children}</em>,
                      hyperlink: ({ node, children }) => (
                        <a
                          href={node.data.url}
                          className="a-underline"
                          target={node.data.target || "_self"}
                          rel={node.data.target === "_blank" ? "noopener noreferrer" : undefined}
                        >
                          {children}
                        </a>
                      ),
                    }}
                  />
                )}

                {/* Nom */}
                {isFilled.keyText(item.name) && (
                  <p className="text-nav">{item.name}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
